'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { LoginFormData, RegisterFormData } from '@/lib/validations/auth';
import AuthForm from '@/components/AuthForm';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function RegisterPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (data: LoginFormData | RegisterFormData) => {
    // Since this is the register page, we know it's RegisterFormData
    const registerData = data as RegisterFormData;
    setIsLoading(true);
    setError(undefined);
    setSuccess(false);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to register');
      }

      setSuccess(true);
      // Redirect to verification page
      setTimeout(() => {
        router.push('/verify?registered=true');
      }, 2000);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to register';
    setError(errorMessage);
    console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <div className="w-full max-w-md">
        {success ? (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <Alert className="mb-4 bg-green-50 text-green-800 border-green-200">
              <AlertDescription>
                Account created successfully! Please check your email to verify your account.
              </AlertDescription>
            </Alert>
            <p className="text-center text-gray-500 mt-4">Redirecting to verification page...</p>
          </div>
        ) : (
          <AuthForm
            type="register"
            onSubmit={handleSubmit}
            isLoading={isLoading}
            error={error}
          />
        )}
      </div>
    </div>
  );
}
